import { useState, useContext, useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import { TostMsg } from "../../../App";
import ERROR_LIST from "../../../Services/Global-Services/Hadle_Errors";
import ComponentLoader from "../../../Shared/Sidebar/ComponentLoader";
import { Link, useHistory } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { getProjectTimeSheet, getTimeSheet, postTimeSheet, postTimeSheetDelete, postTimeSheetWeek, postTimeStatusUpdate } from "./TimeSheetServices";
import dayjs from "dayjs";
import { getProjectTaskList } from "../ProjectManagement/Service/TaskService";
import { createPortal } from "react-dom";

const SearchableDropdown = ({
  value,
  options,
  onSelect,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  disabled = false,
  isInvalid = false,
  renderOption,
  getOptionValue,
  footer = null,
  loading = false,
}) => {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const wrapperRef = useRef(null); // trigger box
  const menuRef = useRef(null);    // portal menu

  const selectedOption = options.find(
    (opt) => String(getOptionValue(opt)) === String(value)
  );
  const displayLabel = selectedOption ? renderOption(selectedOption) : "";

  const filtered = options.filter((opt) =>
    renderOption(opt).toLowerCase().includes(search.toLowerCase())
  );

  const isDisabled = disabled || loading;
  // Calculate position of the menu relative to viewport (fixed positioning)
  const updateMenuPosition = () => {
    if (!wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const menuMaxHeight = 260;
    const openUpwards = spaceBelow < menuMaxHeight && rect.top > menuMaxHeight;

    setMenuStyle({
      position: "fixed",
      left: rect.left,
      width: rect.width,
      top: openUpwards ? undefined : rect.bottom + 2,
      bottom: openUpwards ? viewportHeight - rect.top + 2 : undefined,
      zIndex: 99999,
    });
  };

  useEffect(() => {
    if (!open) return;
    updateMenuPosition();

    const handleReposition = () => updateMenuPosition();
    window.addEventListener("scroll", handleReposition, true); // capture = catches table-responsive scroll too
    window.addEventListener("resize", handleReposition);

    return () => {
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const clickedTrigger = wrapperRef.current && wrapperRef.current.contains(e.target);
      const clickedMenu = menuRef.current && menuRef.current.contains(e.target);
      if (!clickedTrigger && !clickedMenu) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);


  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  const menu = open && !loading && (
    <div
      ref={menuRef}
      style={{
        ...menuStyle,
        background: "#fff",
        border: "1px solid #dee2e6",
        borderRadius: 4,
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        maxHeight: 260,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ padding: "6px 8px", borderBottom: "1px solid #dee2e6", flexShrink: 0 }}>
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          autoFocus
        />
      </div>

      <div style={{ overflowY: "auto", flex: 1 }}>
        {filtered.length === 0 && (
          <div style={{ padding: "8px 12px", color: "#999", fontSize: 13 }}>
            No results found
          </div>
        )}
        {filtered.map((opt) => {
          const optVal = getOptionValue(opt);
          const isSelected = String(value) === String(optVal);
          return (
            <div
              key={optVal}
              style={{
                padding: "8px 12px",
                cursor: "pointer",
                fontSize: 13,
                background: isSelected ? "#f0f4ff" : "transparent",
                fontWeight: isSelected ? 500 : 400,
              }}
              onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = "#f8f9fa"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = isSelected ? "#f0f4ff" : "transparent"; }}
              onClick={() => {
                onSelect(opt);
                setOpen(false);
                setSearch("");
              }}
            >
              {renderOption(opt)}
            </div>
          );
        })}
      </div>

      {footer && (
        <div
          style={{ borderTop: "1px solid #dee2e6", flexShrink: 0 }}
          onClick={() => { setOpen(false); setSearch(""); }}
        >
          {footer}
        </div>
      )}
    </div>
  );

  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      <div
        className={`form-control d-flex align-items-center justify-content-between${isInvalid ? " is-invalid" : ""}`}
        style={{
          cursor: isDisabled ? "not-allowed" : "pointer",
          background: isDisabled ? "#e9ecef" : "#fff",
          minHeight: 33,
          userSelect: "none",
          maxWidth: "151px",
        }}
        onClick={() => { if (!isDisabled) setOpen((prev) => !prev); }}
      >
        <span
          style={{
            color: loading ? "#888" : (displayLabel ? "#212529" : "#6c757d"),
            fontSize: 12,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          {loading && (
            <span
              className="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
              style={{ width: 12, height: 12, borderWidth: 1.5 }}
            />
          )}
          {loading ? "Loading tasks..." : (displayLabel || placeholder)}
        </span>
        {!loading && (
          <span style={{ fontSize: 10, color: "#888", marginLeft: 4, flexShrink: 0 }}>▼</span>
        )}
      </div>

      {menu && createPortal(menu, document.body)}
    </div>
  );
};

const WEEKEND_DAYS = ["Sa", "Su"];
const isWeekendDay = (day) => WEEKEND_DAYS.includes(day);
const WEEKEND_CELL_STYLE = { background: "#eeeeee" };
const WEEKEND_INPUT_STYLE = { background: "#e9ecef", cursor: "not-allowed" };

const DAY_STATUS_PENDING = 101;
const DAY_STATUS_SUBMITTED = 102;
const DAY_STATUS_APPROVED = 103;
const DAY_STATUS_REJECTED = 104;

const isDayLocked = (status) => {
  const s = Number(status);
  return s === DAY_STATUS_APPROVED || s === DAY_STATUS_REJECTED;
};

const getDayStatusStyle = (status) => {
  const s = Number(status);
  if (s === DAY_STATUS_APPROVED) return { background: "#d7f7dc" };
  if (s === DAY_STATUS_REJECTED) return { background: "#fbdada" };
  return undefined;
};

const ALL_DAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
const WEEKDAY_DAYS = ["Mo", "Tu", "We", "Th", "Fr"];

const TimesheetRow = forwardRef(({
  data,
  index,
  editingRows,
  setEditingRows,
  projectList,
  taskList,
  taskListLoading,
  weekDates,
  userDetails,
  toster,
  setIsloading,
  setSelectedProjectId,
  GetTimeSheet,
  TimeSheetRowSchema,
  onRowChange,
  onAddRow,
}, ref) => {
  const rowId = String(data.id);
  const isEdit = editingRows.has(rowId);
  const [isNewTaskMode, setIsNewTaskMode] = useState(false);

  const sanitizeHoursInput = (value) => {
    let cleaned = String(value).replace(/[^\d.]/g, "");

    const firstDotIndex = cleaned.indexOf(".");
    if (firstDotIndex !== -1) {
      cleaned =
        cleaned.slice(0, firstDotIndex + 1) +
        cleaned.slice(firstDotIndex + 1).replace(/\./g, "");
    }

    let [whole = "", fraction = ""] = cleaned.split(".");
    let hasDot = cleaned.includes(".");

    if (fraction.length > 0) {
      const firstFractionDigit = fraction[0];
      fraction = firstFractionDigit === "0" || firstFractionDigit === "5" ? firstFractionDigit : "";
    }

    if (whole !== "") {
      let wholeNum = parseInt(whole, 10);
      if (Number.isNaN(wholeNum)) wholeNum = 0;
      if (wholeNum >= 10) {
        whole = "10";
        fraction = "";
        hasDot = false;
      } else {
        whole = String(wholeNum);
      }
    }

    return hasDot ? `${whole}.${fraction}` : whole;
  };

  const toDecimalNumber = (value) => {
    if (value === null || value === undefined || value === "") return 0;
    const numeric = Number(String(value).trim());
    return Number.isFinite(numeric) ? numeric : 0;
  };

  const formatOneDecimal = (value) => toDecimalNumber(value).toFixed(1);

  const formik = useFormik({
    initialValues: {
      Id: data.Id,
      ProjectId: data.ProjectId != null ? String(data.ProjectId) : "",
      ProjectName: data.ProjectName,
      TimeSheetStatusId: data.TimeSheetStatusId,
      TaskId: data.TaskId != null ? String(data.TaskId) : "",
      Task: data.Task,
      TaskNumber: data.TaskNumber,
      // Tracks whether this row's task was created via the "+ New Task" free-text
      // flow. When true, save logic must always send TaskId 0 (never fall back to
      // any stale per-day TaskId) so the backend creates a brand new task.
      IsNewTask: data.IsNewTask || false,
      Mo: data.Mo,
      Tu: data.Tu,
      We: data.We,
      Th: data.Th,
      Fr: data.Fr,
      Sa: data.Sa,
      Su: data.Su,
      dayIdMap: data.dayIdMap,
      dayTaskIdMap: data.dayTaskIdMap,
      dayStatusMap: data.dayStatusMap,
    },
    validationSchema: TimeSheetRowSchema,
    enableReinitialize: true,
  });

  const handleFieldChange = (field, value) => {
    formik.setFieldValue(field, value);
    if (typeof onRowChange === "function") {
      onRowChange(rowId, { [field]: value });
    }
  };

  useImperativeHandle(ref, () => ({
    getValues: () => formik.values,
    validate: async () => {
      const errors = await formik.validateForm();
      formik.setTouched(
        Object.keys(formik.values).reduce((acc, k) => ({ ...acc, [k]: true }), {})
      );
      return Object.keys(errors).length === 0;
    },
    isEdit,
  }));

  const days = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
  const rowTotal = formatOneDecimal(
    days.reduce((sum, day) => sum + toDecimalNumber(formik.values[day]), 0)
  );

  const hasPendingDay = days.some((d) => Number(data.dayStatusMap?.[d]) === DAY_STATUS_PENDING);
  const hasLockedDay = days.some((d) => isDayLocked(data.dayStatusMap?.[d]));
  const isFullyApprovedRow = WEEKDAY_DAYS.every((d) => Number(data.dayStatusMap?.[d]) === DAY_STATUS_APPROVED);

  const filteredTasksForProject = taskList.filter(
    (task) => String(task.ProjectId) === String(formik.values.ProjectId)
  );

  useEffect(() => {
    if (isEdit && formik.values.ProjectId) {
      setSelectedProjectId(String(formik.values.ProjectId));
    }
  }, [isEdit, formik.values.ProjectId]);

  useEffect(() => {
    if (
      isEdit &&
      !isNewTaskMode &&
      !taskListLoading &&
      formik.values.ProjectId &&
      filteredTasksForProject.length === 1 &&
      !formik.values.TaskId
    ) {
      const singleTask = filteredTasksForProject[0];
      handleFieldChange("TaskId", String(singleTask.TaskId));
      handleFieldChange("Task", singleTask.TaskTitle);
      handleFieldChange("TaskNumber", singleTask.TaskNumber || "");
      handleFieldChange("IsNewTask", false);
    }
  }, [isEdit, isNewTaskMode, taskListLoading, formik.values.ProjectId, filteredTasksForProject]);

  const newTaskFooter = (
    <div
      style={{
        padding: "8px 12px",
        cursor: "pointer",
        color: "#5c67f2",
        fontWeight: 600,
        fontSize: 13,
        display: "flex",
        alignItems: "center",
        gap: 6,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "#f0f4ff")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      onClick={() => {
        setIsNewTaskMode(true);
        // New task mode: TaskId must be 0 (not "") so the save logic can
        // distinguish "explicitly new task" from "nothing selected yet",
        // and the IsNewTask flag ensures save never falls back to a stale
        // per-day TaskId for this row.
        handleFieldChange("TaskId", 0);
        handleFieldChange("Task", "");
        handleFieldChange("TaskNumber", "");
        handleFieldChange("IsNewTask", true);
      }}
    >
      <span style={{ fontSize: 16, lineHeight: 1 }}>+</span> New Task
    </div>
  );
  // const truncateTextByWords = (text, limit) => {
  //   if (!text) return "";
  //   const words = text.split(" ");
  //   return words.length > limit
  //     ? words.slice(0, limit).join(" ") + "..."
  //     : text;
  // };

  const truncateTextByChars = (text, limit) => {
  if (!text) return "";
  return text.length > limit
    ? text.slice(0, limit) + "..."
    : text;
};

  const handleRowSubmitClick = (e, type) => {
    e.stopPropagation();
    const ids = days
      .filter((d) => Number(data.dayStatusMap?.[d]) === DAY_STATUS_PENDING && data.dayIdMap?.[d])
      .map((d) => data.dayIdMap[d]);
    if (typeof data._onSubmitClick === "function") {
      data._onSubmitClick(ids, type);
    }
  };


  if (!isEdit) {
    return (
      <>
        <td>{data.ProjectName || "—"}</td>
        {/* <td>{data.Task || "—"}</td> */}
        <td title={data.Task} style={{
          whiteSpace: "nowrap",
          // overflow: "visible",
          // textOverflow: "unset"
        }}>
          {truncateTextByChars(data.Task, 35)}
        </td>
        <td>{data.TaskNumber || "—"}</td>
        {days.map((day) => {
          const isWeekend = isWeekendDay(day);
          const dayStatus = data.dayStatusMap?.[day];
          const cellStyle = isWeekend ? WEEKEND_CELL_STYLE : getDayStatusStyle(dayStatus);
          return (
            <td key={day} style={cellStyle}>
              <div className="timeinput-box">{data[day]}</div>
            </td>
          );
        })}
        <td>
          <div className="timeinput-box">{data.Total}</div>
        </td>
        <td className="editactions" style={{ minWidth: 110, whiteSpace: "nowrap" }}>
          <div className="d-flex align-items-center justify-content-end gap-2">
            {/* {!isFullyApprovedRow && ( */}
            <button
              type="button"
              className="editactions"
              title="Delete"
              data-bs-toggle="modal"
              data-bs-target="#delete-org-requestAccess"
              // disabled={hasLockedDay}
              onClick={(e) => {
                e.stopPropagation();
                data._onDeleteClick && data._onDeleteClick(data);
              }}
            >
              <img src="/images/tickets/delete.svg" alt="Delete" />
            </button>
            {/* )} */}
            {hasPendingDay && (
              <button
                type="button"
                className="editactions"
                title="Submit for Approval"
                data-bs-toggle="modal"
                data-bs-target="#single-submit-approve-confirm"
                onClick={(e) => handleRowSubmitClick(e, 1)}
              >
                <i className="fa-solid fa-paper-plane"></i>
              </button>
            )}
            {data.isLastRow && (
              <button
                type="button"
                className={`editactions btn-link `}
                title="Add Row"
                // disabled={isSubmitted}
                onClick={(e) => {
                  e.stopPropagation();
                  if (typeof onAddRow === "function") onAddRow();
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-plus-circle" viewBox="0 0 16 16">
                  <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
                  <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4" />
                </svg>
              </button>
            )}
          </div>
        </td>
      </>
    );
  }

  // Edit mode
  return (
    <>
      {/* Project dropdown */}
      <td>
        <SearchableDropdown
          value={formik.values.ProjectId}
          options={projectList}
          placeholder="Select Project"
          searchPlaceholder="Search project..."
          isInvalid={formik.touched.ProjectId && !!formik.errors.ProjectId}
          renderOption={(p) => p.Name}
          getOptionValue={(p) => String(p.Id)}
          onSelect={(p) => {
            const val = String(p.Id);
            formik.setFieldValue("ProjectId", val);
            formik.setFieldValue("ProjectName", p.Name);
            formik.setFieldValue("Task", "");
            formik.setFieldValue("TaskId", "");
            formik.setFieldValue("TaskNumber", "");
            formik.setFieldValue("IsNewTask", false);
            if (typeof onRowChange === "function") {
              onRowChange(rowId, {
                ProjectId: val,
                ProjectName: p.Name,
                Task: "",
                TaskId: "",
                TaskNumber: "",
                IsNewTask: false,
              });
            }
            setSelectedProjectId(val);
            setIsNewTaskMode(false);
          }}
        />
        {formik.touched.ProjectId && formik.errors.ProjectId && (
          <div className="invalid-feedback d-block">{formik.errors.ProjectId}</div>
        )}
      </td>

      <td>
        {isNewTaskMode ? (
          <div className="d-flex align-items-center gap-1">
            <input
              type="text"
              className={`task-input  form-control ${formik.touched.Task && formik.errors.Task ? "is-invalid" : ""}`}
              placeholder="Enter new task name"
              value={formik.values.Task}
              onChange={(e) => {
                handleFieldChange("Task", e.target.value);
                // Keep TaskId explicitly 0 (numeric) and IsNewTask true while
                // the user is typing a brand-new task name.
                handleFieldChange("TaskId", 0);
                handleFieldChange("TaskNumber", "");
                handleFieldChange("IsNewTask", true);
              }}
              autoFocus
            />
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              style={{ whiteSpace: "nowrap" }}
              onClick={() => {
                setIsNewTaskMode(false);
                handleFieldChange("Task", "");
                handleFieldChange("TaskId", "");
                handleFieldChange("TaskNumber", "");
                handleFieldChange("IsNewTask", false);
              }}
              title="Back to list"
            >
              ✕
            </button>
            {formik.touched.Task && formik.errors.Task && (
              <div className="invalid-feedback d-block">{formik.errors.Task}</div>
            )}
          </div>
        ) : (
          <>
            <SearchableDropdown
              value={formik.values.TaskId}
              options={filteredTasksForProject}
              placeholder="Select Task"
              searchPlaceholder="Search task..."
              disabled={!formik.values.ProjectId}
              loading={taskListLoading}
              isInvalid={formik.touched.Task && !!formik.errors.Task}
              renderOption={(t) => t.TaskTitle}
              getOptionValue={(t) => String(t.TaskId)}
              onSelect={(t) => {
                handleFieldChange("TaskId", String(t.TaskId));
                handleFieldChange("Task", t.TaskTitle);
                handleFieldChange("TaskNumber", t.TaskNumber || "");
                handleFieldChange("IsNewTask", false);
              }}
              footer={newTaskFooter}
            />
            {formik.touched.Task && formik.errors.Task && (
              <div className="invalid-feedback d-block">{formik.errors.Task}</div>
            )}
          </>
        )}
      </td>

      <td>
        <div className="timeinput-box">{formik.values.TaskNumber || "—"}</div>
      </td>

      {days.map((day) => {
        const isWeekend = isWeekendDay(day);
        const dayStatus = formik.values.dayStatusMap?.[day];
        const locked = !isWeekend && isDayLocked(dayStatus);
        const isDisabled = isWeekend || locked;
        const cellStyle = isWeekend ? WEEKEND_CELL_STYLE : getDayStatusStyle(dayStatus);
        const inputStyle = isWeekend ? WEEKEND_INPUT_STYLE : (locked ? getDayStatusStyle(dayStatus) : undefined);
        return (
          <td key={day} style={cellStyle}>
            <input
              type="text"
              name={day}
              value={formik.values[day]}
              onChange={(e) => {
                if (isDisabled) return;
                handleFieldChange(day, sanitizeHoursInput(e.target.value));
              }}
              className={`form-control ${formik.touched[day] && formik.errors[day] ? "is-invalid" : ""}`}
              placeholder="0.0"
              disabled={isDisabled}
              style={inputStyle}
            />
            {formik.touched[day] && formik.errors[day] && (
              <div className="invalid-feedback d-block">{formik.errors[day]}</div>
            )}
          </td>
        );
      })}

      <td>
        <div className="timeinput-box">{rowTotal}</div>
      </td>

      <td style={{ minWidth: 90, whiteSpace: "nowrap" }}>
        <div className="d-flex align-items-center justify-content-end gap-2">
          {data.isLastRow && (
            <button
              className="editactions me-2 btn btn-link"
              title="Add Row"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (typeof onAddRow === "function") onAddRow();
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-plus-circle" viewBox="0 0 16 16">
                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
                <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4" />
              </svg>
            </button>
          )}
        </div>
      </td>
    </>
  );
});

const TimesheetList = () => {
  const [Isloading, setIsloading] = useState(false);

  const [approvedTime, setApprovedTime] = useState("0.0");
  const [submittedTime, setSubmittedTime] = useState("0.0");
  const [unsubmittedTime, setUnsubmittedTime] = useState("0.0");
  const [editingRows, setEditingRows] = useState(new Set());
  const [dirtyRows, setDirtyRows] = useState(new Set());
  const [timesheetList, setTimesheetList] = useState([]);
  const [projectList, setProjectList] = useState([]);
  const [weekDates, setWeekDates] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [taskList, setTaskList] = useState([]);
  const [taskListLoading, setTaskListLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [singleSubmitTarget, setSingleSubmitTarget] = useState(null);
  const [statusArr, setStatusArr] = useState([]);
  const [typeArr, setTypeArr] = useState([]);
  const [clientArr, setClientArr] = useState([]);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [search, setSearch] = useState("");
  const [sortByVal, setSortByVal] = useState("");
  const [sortOrderVal, setSortOrderVal] = useState("");
  const rowRefs = useRef({});
  const originalRowValues = useRef({});
  const isSavingRef = useRef(false);
  const isSubmittingRef = useRef(false);
  let userDetails = JSON.parse(localStorage.getItem("User") || "{}");
  const toster = useContext(TostMsg);
  const history = useHistory();
  const getMonday = (date) => {
    const d = dayjs(date);
    const day = d.day();
    return d.subtract(day === 0 ? 6 : day - 1, "day");
  };

  const [currentStart, setCurrentStart] = useState(() => getMonday(dayjs()));

  useEffect(() => {
    const start = getMonday(currentStart);
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = start.add(i, "day");
      dates.push({ label: date.format("ddd, DD MMM"), iso: date.format("YYYY-MM-DD") });
    }
    setWeekDates(dates);
  }, [currentStart]);

  const handlePrevWeek = () => setCurrentStart((prev) => prev.subtract(7, "day"));
  const handleNextWeek = () => setCurrentStart((prev) => prev.add(7, "day"));
  const startDate = currentStart.startOf("isoWeek").format("DD MMM YYYY");
  const endDate = currentStart.startOf("isoWeek").add(6, "day").format("DD MMM YYYY");

  const generateUniqueId = (() => {
    let counter = 0;
    return () => `${Date.now()}_${++counter}_${Math.floor(Math.random() * 1000000)}`;
  })();

  const formatHours = (value) => {
    if (value === null || value === undefined || value === "") return "0.0";
    const numeric = Number(String(value).trim());
    return Number.isFinite(numeric) ? numeric.toFixed(1) : "0.0";
  };

  const getHoursValue = (value) => {
    if (value === null || value === undefined || value === "") return 0;
    const text = String(value).trim();
    if (!text) return 0;

    const numeric = Number(text);
    return Number.isFinite(numeric) ? numeric : 0;
  };

  const initializeRow = () => ({
    Id: 0,
    ProjectId: "",
    ProjectName: "",
    TimeSheetStatusId: "",
    TaskId: "",
    Task: "",
    TaskNumber: "",
    // See TimesheetRow formik initialValues for details on this flag.
    IsNewTask: false,
    Mo: "",
    Tu: "",
    We: "",
    Th: "",
    Fr: "",
    Sa: "",
    Su: "",
    Total: "0.0",
    id: generateUniqueId(),
    TimeSheetDate: "",
    dayIdMap: "",
    dayTaskIdMap: "",
    dayTaskName: "",
    dayStatusMap: { Mo: null, Tu: null, We: null, Th: null, Fr: null, Sa: null, Su: null },
    isDirty: false,
  });

  const calculateRowTotal = (row) => {
    const days = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
    const totalHours = days.reduce((sum, day) => sum + getHoursValue(row[day]), 0);
    return formatHours(totalHours);
  };

  const updateRowData = (rowId, changes) => {
    setDirtyRows((prev) => new Set([...prev, String(rowId)]));

    setTimesheetList((prev) =>
      prev.map((row) => {
        if (String(row.id) !== String(rowId)) return row;
        const updated = { ...row, ...changes };
        if (Object.keys(changes).some((field) => ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].includes(field))) {
          updated.Total = calculateRowTotal(updated);
        }
        return updated;
      })
    );
  };

  const HOURS_REGEX = /^$|^(10|[0-9])(\.[05]?)?$/;

  const TimeSheetRowSchema = Yup.object().shape({
    ProjectId: Yup.string().required("Project is required"),
    Task: Yup.string().required("Task is required"),
    Mo: Yup.string().matches(HOURS_REGEX, "Use 0.5 hour increments, max 10"),
    Tu: Yup.string().matches(HOURS_REGEX, "Use 0.5 hour increments, max 10"),
    We: Yup.string().matches(HOURS_REGEX, "Use 0.5 hour increments, max 10"),
    Th: Yup.string().matches(HOURS_REGEX, "Use 0.5 hour increments, max 10"),
    Fr: Yup.string().matches(HOURS_REGEX, "Use 0.5 hour increments, max 10"),
    Sa: Yup.string().matches(HOURS_REGEX, "Use 0.5 hour increments, max 10"),
    Su: Yup.string().matches(HOURS_REGEX, "Use 0.5 hour increments, max 10"),
  });

  useEffect(() => {
    GetProjectTimeSheet();
    GetTimeSheet();
  }, [currentStart]);

  useEffect(() => {
    let unsubmittedHours = 0;
    timesheetList.forEach((row) => {
      const statusId = row.TimeSheetStatusId;
      const isNewOrUnsaved =
        Number(statusId) === 101 ||
        statusId === "" ||
        statusId === null ||
        statusId === undefined;
      if (isNewOrUnsaved) {
        unsubmittedHours += getHoursValue(row.Total);
      }
    });
    setUnsubmittedTime(formatHours(unsubmittedHours));
  }, [timesheetList]);

  useEffect(() => { console.log(selectedProjectId); getSubTaskList(selectedProjectId); }, [selectedProjectId]);

  async function getSubTaskList(projectId) {
    const params = {
      RaisedByEmpId: "",
      AssignedToId: '',
      TaskId: 0,
      StatusId: "",
      ModuleId: "",
      SubModuleId: "",
      PriorityId: "",
      SeverityId: "",
      Search: "",
      FilterStartDate: "2020-01-01",
      FilterEndDate: "2050-01-01",
      PageNumber: 1,
      PageSize: 500,
      IsDeleted: 0,
      SprintId: 0,
      TaskNumber: "",
      TaskGUID: "",
      ProjectId: projectId || "0",
      ParentTaskId: 0,
      TaskTypeId: "",
      SegmentId: "",
      SortBy: "",
      SortOrder: "ASC",
    };
    setTaskListLoading(true);
    try {
      const data = await getProjectTaskList(params);
      setTaskList(data?.Data?.OrgEmpTasks || []);
    } catch (error) {
      console.log(error);
    } finally {
      setTaskListLoading(false);
    }
  }

  async function GetProjectTimeSheet() {
    let params = {
      Id: 0,
      Guid: ''
    };
    try {
      const data = await getProjectTimeSheet(params);
      setProjectList(data?.Data || []);
    } catch (error) { }
  }

  async function GetTimeSheet() {
    setIsloading(true);
    const body = {
      EmpId: userDetails?.EmpId,
      ClientId: 0,
      ProjectId: 0,
      StatusId: 0,
      GroupBy: 3,
      HierarchyTypeId: 0,
      FetchType: 0,
      BillableType: 0,
      StartDate: String(startDate),
      EndDate: String(endDate),
    };
    try {
      const response = await getTimeSheet(body);
      const groupResponses = response?.Data?.TimeSheetResponses || [];
      const rawEntries = groupResponses.flatMap((group) => group.TimeSheets || []);
      let approvedSum = 0;
      let pendingSum = 0;
      groupResponses.forEach((group) => {
        approvedSum += getHoursValue(group.ApprovedHours);
        pendingSum += getHoursValue(group.PendingHours);
      });
      setApprovedTime(formatHours(approvedSum));
      setSubmittedTime(formatHours(pendingSum));
      const getDayKey = (isoDate) => {
        const date = dayjs(isoDate);
        if (!date.isValid()) return null;
        const map = { Mo: "Mo", Tu: "Tu", We: "We", Th: "Th", Fr: "Fr", Sa: "Sa", Su: "Su" };
        return map[date.format("dd")] || null;
      };

      const makeEmptySlot = (item) => ({
        Id: item?.Id,
        ProjectId: item.ProjectId,
        ProjectName: item.ProjectName,
        TimeSheetStatusId: item.TimeSheetStatusId,
        Task: item.Task,
        TaskId: item?.TaskId,
        TaskNumber: item?.TaskNumber,
        dayMap: { Mo: 0, Tu: 0, We: 0, Th: 0, Fr: 0, Sa: 0, Su: 0 },
        totalHours: 0,
        dayIdMap: { Mo: null, Tu: null, We: null, Th: null, Fr: null, Sa: null, Su: null },
        dayTaskIdMap: { Mo: null, Tu: null, We: null, Th: null, Fr: null, Sa: null, Su: null },
        dayStatusMap: { Mo: null, Tu: null, We: null, Th: null, Fr: null, Sa: null, Su: null },
        dayTaskName: {},
      });
      const grouped = {};
      rawEntries.forEach((item) => {
        const dayKey = getDayKey(item.TimeSheetDate);
        if (!dayKey) return;

        const key = `${item.ProjectId}-${item.Task}`;
        if (!grouped[key]) grouped[key] = [];
        const slots = grouped[key];

        let slot = slots.find((s) => !s.dayIdMap[dayKey]);
        if (!slot) {
          slot = makeEmptySlot(item);
          slots.push(slot);
        }

        const hours = getHoursValue(item.Hours);
        slot.dayMap[dayKey] += hours;
        slot.totalHours += hours;
        slot.dayIdMap[dayKey] = item.Id;
        slot.dayTaskIdMap[dayKey] = item.TaskId;
        slot.dayTaskName[dayKey] = item.Task;
        slot.dayStatusMap[dayKey] = item.TimeSheetStatusId;
        slot.TimeSheetStatusId = item.TimeSheetStatusId;
      });

      const rows = [];
      Object.values(grouped).forEach((slots) => {
        slots.forEach((slot) => {
          const row = initializeRow();
          row.ProjectId = slot.ProjectId;
          row.ProjectName = slot.ProjectName;
          row.TimeSheetStatusId = slot.TimeSheetStatusId;
          row.Task = slot.Task;
          row.TaskId = slot.TaskId;
          row.TaskNumber = slot.TaskNumber;
          row.Total = formatHours(slot.totalHours);
          row.Id = slot.Id;
          row.dayIdMap = slot.dayIdMap;
          row.dayTaskIdMap = slot.dayTaskIdMap;
          row.dayTaskName = slot.dayTaskName;
          row.dayStatusMap = slot.dayStatusMap;
          Object.keys(slot.dayMap).forEach((day) => { row[day] = formatHours(slot.dayMap[day]); });
          rows.push(row);
        });
      });

      rows.sort((a, b) => a.ProjectName.localeCompare(b.ProjectName) || a.Task.localeCompare(b.Task));
      setTimesheetList(rows);
      setEditingRows(new Set());
      setDirtyRows(new Set());
      return rows;
    } catch (err) {
      toster.current.error("Failed to load timesheet");
      setTimesheetList([initializeRow()]);
      return [];
    } finally {
      setIsloading(false);
    }
  }

  const isRowIncomplete = (row) => {
    if (!row) return true;
    return !row.ProjectId || !row.Task;
  };

  const hasIncompleteEditingRow = () => {
    for (const id of editingRows) {
      const row = timesheetList.find((r) => String(r.id) === String(id));
      if (isRowIncomplete(row)) return true;
    }
    return false;
  };

  // A row is "blank / never-saved" when it was created locally via handleAddRow
  // (Id === 0, i.e. it has no server-side timesheet id) and the user hasn't filled
  // in Project/Task for it yet. Such a row can be safely discarded instead of
  // blocking the user from opening a different row for editing.
  const isBlankUnsavedRow = (row) => {
    if (!row) return false;
    return Number(row.Id) === 0 && isRowIncomplete(row);
  };

  // If the row(s) currently in edit mode are blank/unsaved and incomplete, silently
  // remove them (except the one we're about to open, if any) instead of showing an
  // error. Returns true if something was discarded.
  const discardBlankEditingRows = (exceptRowId) => {
    const idsToDiscard = [];
    editingRows.forEach((id) => {
      if (exceptRowId != null && String(id) === String(exceptRowId)) return;
      const row = timesheetList.find((r) => String(r.id) === String(id));
      if (isBlankUnsavedRow(row)) {
        idsToDiscard.push(String(id));
      }
    });

    if (idsToDiscard.length === 0) return false;

    setTimesheetList((prev) => prev.filter((r) => !idsToDiscard.includes(String(r.id))));
    setDirtyRows((prev) => {
      const next = new Set(prev);
      idsToDiscard.forEach((id) => next.delete(id));
      return next;
    });
    idsToDiscard.forEach((id) => {
      delete rowRefs.current[id];
    });

    return true;
  };

  // Handles clicking a row to open it for editing. If a blank/empty "add row" is
  // currently open and untouched, it gets discarded automatically so the user can
  // switch to editing the clicked row without seeing a blocking error.
  const handleRowEditClick = (row) => {
    const rowIdStr = String(row.id);
    const alreadyEditingThis = editingRows.has(rowIdStr);
    if (alreadyEditingThis) return;

    const discarded = discardBlankEditingRows(rowIdStr);

    if (!discarded && hasIncompleteEditingRow()) {
      toster.current.error("Please complete Project and Task in the current row before opening another.");
      return;
    }

    setEditingRows(new Set([rowIdStr]));
  };


  const handleAddRow = () => {
    if (hasIncompleteEditingRow()) {
      toster.current.error("Please complete Project and Task in the current row before adding a new one.");
      return;
    }
    const newRow = initializeRow();
    setTimesheetList((prev) => [...prev, newRow]);
    setEditingRows(new Set([String(newRow.id)]));
    setDirtyRows((prev) => new Set([...prev, String(newRow.id)]));
  };

  const handleSaveAll = async () => {
    if (isSavingRef.current) return null;
    isSavingRef.current = true;

    try {
      if (dirtyRows.size === 0) {
        toster.current.success("All timesheets are already saved.");
        return null;
      }

      const days = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
      const hasAnyHours = (values) => days.some((day) => getHoursValue(values[day]) > 0);
      let allValid = true;
      let firstInvalidRowId = null;
      for (const rowId of dirtyRows) {
        const rowRef = rowRefs.current[rowId];
        if (!rowRef) continue;
        const values = rowRef.getValues();
        if (!hasAnyHours(values)) continue;
        const valid = await rowRef.validate();
        if (!valid) {
          allValid = false;
          if (!firstInvalidRowId) firstInvalidRowId = rowId;
        }
      }

      if (!allValid) {
        if (firstInvalidRowId) {
          setEditingRows(new Set([firstInvalidRowId]));
        }
        toster.current.error("Please fix validation errors before saving.");
        return null;
      }

      const allTimeSheets = [];
      for (const rowId of dirtyRows) {
        const rowRef = rowRefs.current[rowId];
        if (!rowRef) continue;
        const values = rowRef.getValues();

        // TaskId resolution:
        // - If this row is in "+ New Task" mode (values.IsNewTask), we always
        //   send TaskId 0 so the backend creates a brand-new task from the
        //   Task name. We must NOT fall back to any stale per-day TaskId
        //   here — otherwise a row that previously had a task selected (and
        //   is now being re-typed as a new task) would incorrectly send the
        //   OLD TaskId instead of creating a new one.
        // - Otherwise, prioritize the currently selected formik TaskId, and
        //   only fall back to the old per-day TaskId when nothing was
        //   explicitly selected (e.g. an untouched previously-saved day).
        const resolveTaskId = (day) => {
          if (values.IsNewTask) return 0;
          if (values.TaskId !== "" && values.TaskId !== null && values.TaskId !== undefined) {
            return Number(values.TaskId);
          }
          return values.dayTaskIdMap?.[day] || 0;
        };

        days.forEach((day, i) => {
          const hours = values[day];
          const hourValue = getHoursValue(hours);
          const dayId = values.dayIdMap?.[day];

          // Skip only when there are no hours AND this day never had a saved
          // entry (no dayId). If a dayId already exists (this day was
          // previously saved with hours > 0), we must still send it — even
          // when the new value is 0.0 — so the backend knows to update /
          // zero-out that entry instead of silently ignoring the change.
          if (hourValue === 0 && !dayId) return;

          const taskId = resolveTaskId(day);

          allTimeSheets.push({
            Id: dayId || 0,
            TaskId: taskId,
            TimeSheetDate: weekDates[i]?.iso || "",
            ProjectId: Number(values.ProjectId),
            Task: values.Task,
            Hours: hourValue.toFixed(1),
          });
        });
      }

      if (allTimeSheets.length === 0) {
        toster.current.error("No hours entered to save.");
        return null;
      }

      setIsloading(true);
      try {
        const body = { EmpId: userDetails?.EmpId, TimeSheets: allTimeSheets };
        await postTimeSheet(body);
        toster.current.success("Timesheets saved successfully!");
        setDirtyRows(new Set());
        const rows = await GetTimeSheet();
        await getSubTaskList('0');
        return rows;
      } catch (err) {
        toster.current.error("Save failed. Please try again.");
        return null;
      } finally {
        setIsloading(false);
      }
    } finally {
      isSavingRef.current = false;
    }
  };

  function convertDateFormat(dateString) {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}T11:00:00`;
  }

  async function timeSheetDelete() {
    if (!deleteTarget) return;
    console.log(deleteTarget);
    setIsloading(true);
    const taskId = Object.values(deleteTarget?.dayTaskIdMap || {}).find(id => id !== null) || deleteTarget?.TaskId;
    const body = {
      ProjectId: deleteTarget?.ProjectId,
      TaskId: String(taskId),
      TimeSheetStartDate: convertDateFormat(startDate),
      TimeSheetEndDate: convertDateFormat(endDate),
    };
    try {      
      const res = await postTimeSheetDelete(body);
      if (res) GetTimeSheet();
      toster.current.success("Timesheet deleted successfully.");
    } catch (error) {
      toster.current.error(ERROR_LIST.DefaultError);
    } finally {
      setIsloading(false);
    }
  }

  const PostTimeSheetWeek = async () => {
    setIsloading(true);
    const body = {
      OrgId: userDetails?.OrgId,
      LoggedInUserId: userDetails?.EmpId,
      StartDate: currentStart.format("YYYY-MM-DDT00:00:00"),
    };
    try {
      const res = await postTimeSheetWeek(body);
      setIsloading(false);
      if (res?.StatusCode === 2000) {
        GetTimeSheet();
        toster.current.success("Week clone successfully");
      } else {
        toster.current.error(ERROR_LIST.DefaultError);
      }
    } catch (error) {
      setIsloading(false);
      toster.current.error(ERROR_LIST.DefaultError);
    }
  };

  const handleClonePreviousWeek = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    PostTimeSheetWeek();
  };

  const bulkStatusUpdate = async (ids, statusId, type) => {
    if (!ids || ids.length === 0) {
      toster.current.error("No timesheets to update.");
      return;
    }

    setIsloading(true);
    const xmlPayload = `<root>${ids.map((id) => `<TimeSheet><Id>${id}</Id></TimeSheet>`).join("")}</root>`;

    const params = {
      TimeSheetId: xmlPayload,
      StatusId: statusId,
    };

    try {
      await postTimeStatusUpdate(params);
      toster.current.success(
        statusId === 103
          ? `${ids.length} timesheet(s) approved successfully`
          : statusId === 102
            ? `${ids.length} timesheet(s) submitted for approval successfully`
            : `${ids.length} timesheet(s) rejected successfully`
      );
      GetTimeSheet();
      setEditingRows(new Set());
      if (type !== 1) {
        history.push("/Timesheet/Summary");
      }
    } catch (error) {
      toster.current.error("Failed to update status");
    } finally {
      setIsloading(false);
    }
  };
  const handleSubmitForApprovalConfirmed = async () => {
    if (isSubmittingRef.current) return; // ✅ NEW
    isSubmittingRef.current = true;

    try {
      let latestRows = timesheetList;

      if (dirtyRows.size > 0) {
        const saved = await handleSaveAll();
        if (!saved) return;
        latestRows = saved;
      }

      const ids = [];
      latestRows.forEach((row) => {
        const dsm = row.dayStatusMap || {};
        Object.keys(dsm).forEach((day) => {
          if (Number(dsm[day]) === 101 && row.dayIdMap?.[day]) {
            ids.push(row.dayIdMap[day]);
          }
        });
      });

      if (ids.length === 0) {
        toster.current.error("No pending timesheets to submit.");
        return;
      }

      await bulkStatusUpdate(ids, 102, 1);
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const addRowBlocked = hasIncompleteEditingRow();
  const DAY_ORDER = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
  const dayWiseTotals = DAY_ORDER.reduce((acc, day) => {
    acc[day] = timesheetList.reduce((sum, row) => sum + getHoursValue(row[day]), 0);
    return acc;
  }, {});
  const weekGrandTotal = DAY_ORDER.reduce((sum, day) => sum + dayWiseTotals[day], 0);

  const columns = [
    "Project*",
    "Task",
    "Task Number",
    ...weekDates.map((d) => <>{d.label} <br /> 0.0</>),
    <>Total <br /> 0.0</>,
  ];

  const handleBackgroundClick = async () => {
    if (dirtyRows.size > 0) {
      await handleSaveAll();
      return;
    }
    setEditingRows(new Set());
  };

  const rowsWithActions = timesheetList.map((row, i) => ({
    ...row,
    isLastRow: i === timesheetList.length - 1,
    _onDeleteClick: (r) => setDeleteTarget(r),
    _onSubmitClick: (ids, type) => setSingleSubmitTarget({ ids, type }),
    _onCloneClick: () => {
      PostTimeSheetWeek();
    },
    _onChange: (changes) => updateRowData(String(row.id), changes),
  }));

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-xl-12">
          <div className="form-head d-flex flex-wrap align-items-center justify-content-between">
            <div className="me-auto">
              <h2 className="font-w600 mb-0">Timesheet Entry</h2>
              <p>Manage your tasks, projects, and workspaces in one place.</p>
            </div>
            <div className="rightside-tabs">
              <div className="d-flex align-items-center gap-1">
                <a className="btn btn-info text-white" title="Task help" href="/Timesheet/TimesheetHowItWorks">
                  <i className="fa fa-info" aria-hidden="true"></i>
                </a>
                <button type="button" className="btn-og btn-og-secondary btn-og-sm" onClick={handleClonePreviousWeek} disabled={Isloading}>
                  Clone Previous Week
                </button>
                {/* <Link to="/"> */}
                <button
                  type="button"
                  className="btn btn-primary btn-sm text-white"
                  data-bs-toggle="modal"
                  data-bs-target="#bulk-submit-approve-confirm"
                  disabled={Isloading}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" className="bi bi-calendar-week" viewBox="0 0 16 16">
                    <path d="M11 6.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm-3 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm-5 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5zm3 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5z" />
                    <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5M1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4z" />
                  </svg>{" "}
                  Submit for Approval
                </button>
                {/* </Link> */}
                <button className="btn btn-success btn-sm" onClick={handleSaveAll} disabled={Isloading}>
                  Save
                </button>
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-between align-items-center timesheet-range">
            <div className="d-flex align-items-center ">
              <div className="timeclick-box me-2 week-nav">
                <button className="btn btn-default nav-btn" onClick={handlePrevWeek}>←</button>
                <span className="week-range">{startDate} — {endDate}</span>
                <button className="btn btn-default nav-btn" onClick={handleNextWeek}>→</button>
              </div>
            </div>
            <div className=" time-stats">
              <div className="time-stat ts-approved">
                <span className="time-stat-dot"></span>
                <span className="time-label">Approved time</span>
                <span className="time-value">: {approvedTime}</span>
              </div>
              <div className="stat-sep"></div>
              <div className="time-stat ts-submitted">
                <span className="time-stat-dot"></span>
                <span className="time-label">Pending approval time </span>
                <span className="time-value">: {submittedTime}</span>
              </div>
              <div className="stat-sep"></div>
              <div className="time-stat ts-unsub">
                <span className="time-stat-dot"></span>
                <span className="time-label">Unsubmitted time </span>
                <span className="time-value">: {unsubmittedTime}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-12 mt-4">
          <div className="card">
            <div className="card-body p-0" onClick={handleBackgroundClick}>
              <div className="table-responsive">
                <table className="table dtbl timesheet">
                  <thead>
                    <tr>
                      <th>Project*</th>
                      <th>Task</th>
                      <th>Task Number</th>
                      {weekDates.map((d, idx) => {
                        const dow = dayjs(d.iso).format("dd");
                        const isWeekend = isWeekendDay(dow);
                        return (
                          <th key={idx} style={isWeekend ? WEEKEND_CELL_STYLE : undefined}>
                            {d.label} <br /> 0.0
                          </th>
                        );
                      })}
                      <th>Total <br /> 0.0</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rowsWithActions.length === 0 ? (
                      <tr >
                        <td colSpan={columns.length + 1} className="text-center py-4">
                          <div>No Data Found</div>
                          <button
                            type="button"
                            className="btn btn-link mt-3"
                            onClick={handleAddRow}
                            disabled={Isloading}
                            title="Add Timesheet"
                          >
                            + Add Timesheet
                          </button>
                        </td>
                      </tr>
                    ) : (
                      rowsWithActions.map((row, i) => {
                        const projectId = String(row.ProjectId || "").trim();
                        const isClickable = Boolean(projectId) || row.isLastRow;
                        const isLocked = WEEKDAY_DAYS.every((d) => Number(row.dayStatusMap?.[d]) === 103);
                        const canOpenEdit = isClickable && !isLocked;

                        return (
                          <tr
                            key={row.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!canOpenEdit) return;
                              handleRowEditClick(row);
                            }}
                            style={{ cursor: canOpenEdit ? "pointer" : "default" }}
                          >
                            <TimesheetRow
                              ref={(el) => {
                                if (!row?.id) return;
                                if (el) {
                                  rowRefs.current[String(row.id)] = el;
                                } else {
                                  delete rowRefs.current[String(row.id)];
                                }
                              }}
                              data={row}
                              index={i}
                              editingRows={editingRows}
                              setEditingRows={setEditingRows}
                              projectList={projectList}
                              taskList={taskList}
                              taskListLoading={taskListLoading}
                              weekDates={weekDates}
                              userDetails={userDetails}
                              toster={toster}
                              setIsloading={setIsloading}
                              setSelectedProjectId={setSelectedProjectId}
                              GetTimeSheet={GetTimeSheet}
                              TimeSheetRowSchema={TimeSheetRowSchema}
                              onRowChange={updateRowData}
                              onAddRow={handleAddRow}
                            />
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                  <tfoot>
                    <tr className="timesheet-footer-totals" style={{ background: "#f5f5f5", fontWeight: 600 }}>
                      <td colSpan={3} className="text-end">Day-wise / Week Total</td>
                      {DAY_ORDER.map((day) => (
                        <td key={day} style={isWeekendDay(day) ? WEEKEND_CELL_STYLE : undefined}>
                          {formatHours(dayWiseTotals[day])}
                        </td>
                      ))}
                      <td>{formatHours(weekGrandTotal)}</td>
                      <td></td>
                    </tr>
                    <tr>
                      <td colSpan={columns.length + 1}>
                        <button className="btn btn-link" onClick={handleClonePreviousWeek} title="Clone Previous Week" disabled={Isloading}>
                          <i className="fa-solid fa-copy"></i>Clone Previous Week
                        </button>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      <div className="modal fade" id="delete-org-requestAccess">
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content delete-holiday">
            <div className="modal-header">
              <img src="/images/org-img/leave/popup-backimg.png" className="backimg" />
              <div className="img">
                <img src="/images/org-img/leave/trash.png" />
              </div>
            </div>
            <div className="modal-body text-center">
              <strong className="modal-title">Are you sure?</strong>
              <p>Only items with a status of "Pending" or "Unsubmitted" can be deleted. Are you sure you want to proceed?</p>
            </div>
            <div className="modal-footer m-auto">
              <button type="button" className="cancelbtn-click" data-bs-dismiss="modal">
                Cancel
              </button>
              <button
                type="button"
                className="deletebtn-click"
                data-bs-dismiss="modal"
                onClick={timeSheetDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Submit for Approval Confirm Modal (top "Submit for Approval" button) */}
      <div className="modal fade" id="bulk-submit-approve-confirm">
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content delete-holiday">
            <div className="modal-header">
              <img src="/images/org-img/leave/popup-backimg.png" className="backimg" />
              <div className="img">
                <img src="/images/org-img/leave/trash.png" />
              </div>
            </div>
            <div className="modal-body text-center">
              <strong className="modal-title">Are you sure?</strong>
              <p>Are you sure you want to approve all selected items? This action will update their status in bulk.</p>
            </div>
            <div className="modal-footer m-auto">
              <button type="button" className="cancelbtn-click" data-bs-dismiss="modal">
                Cancel
              </button>
              <button
                type="button"
                className="deletebtn-click"
                data-bs-dismiss="modal"
                onClick={handleSubmitForApprovalConfirmed}
              >
                Yes, Submit
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Single-row Submit for Approval Confirm Modal (paper-plane icon) */}
      <div className="modal fade" id="single-submit-approve-confirm">
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content delete-holiday">
            <div className="modal-header">
              <img src="/images/org-img/leave/popup-backimg.png" className="backimg" />
              <div className="img">
                <img src="/images/org-img/leave/trash.png" />
              </div>
            </div>
            <div className="modal-body text-center">
              <strong className="modal-title">Are you sure?</strong>
              <p>Are you sure you want to approve this item?</p>
            </div>
            <div className="modal-footer m-auto">
              <button
                type="button"
                className="cancelbtn-click"
                data-bs-dismiss="modal"
                onClick={() => setSingleSubmitTarget(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="deletebtn-click"
                data-bs-dismiss="modal"
                onClick={() => {
                  if (singleSubmitTarget) {
                    bulkStatusUpdate(singleSubmitTarget.ids, 102, singleSubmitTarget.type);
                    setSingleSubmitTarget(null);
                  }
                }}
              >
                Yes, Approve
              </button>
            </div>
          </div>
        </div>
      </div>

      {Isloading && <ComponentLoader />}
    </div>
  );
};

export default TimesheetList;